function isPlainObject(value) {
  return value && typeof value === "object" && !Array.isArray(value);
}

function cleanText(value, fallback = "") {
  if (value == null) return fallback;
  if (Array.isArray(value)) {
    const joined = value.map((item) => cleanText(item, "")).filter(Boolean).join("\n");
    return joined || fallback;
  }
  if (isPlainObject(value)) {
    return cleanText(value.body || value.text || value.copy || value.description || value.statement || value.title || value.name, fallback);
  }
  const text = String(value).replace(/\r\n/g, "\n").trim();
  if (!text || /^(undefined|null|nan)$/i.test(text)) return fallback;
  return text;
}

function firstField(candidates, sourceFieldsUsed, warnings) {
  for (const [path, value] of candidates) {
    const text = cleanText(value, "");
    if (text) {
      sourceFieldsUsed.push(path);
      return text;
    }
  }
  if (warnings) warnings.push(`missing:${candidates[0]?.[0] || "field"}`);
  return "";
}

function firstDistinctField(candidates, excluded, sourceFieldsUsed, warnings) {
  for (const [path, value] of candidates) {
    const text = cleanText(value, "");
    if (!text) continue;
    if (excluded.some((item) => nearlySameText(text, item))) continue;
    sourceFieldsUsed.push(path);
    return text;
  }
  if (warnings) warnings.push(`missing:${candidates[0]?.[0] || "field"}`);
  return "";
}

function normalizeComparable(value) {
  return cleanText(value, "")
    .toLowerCase()
    .replace(/[’']/g, "'")
    .replace(/&/g, "and")
    .replace(/[^a-z0-9]+/g, " ")
    .trim();
}

function nearlySameText(a, b) {
  const left = normalizeComparable(a);
  const right = normalizeComparable(b);
  if (!left || !right) return false;
  if (left === right) return true;
  const shorter = left.length < right.length ? left : right;
  const longer = left.length < right.length ? right : left;
  return shorter.length > 24 && longer.includes(shorter);
}

function listFrom(value) {
  if (!value) return [];
  if (Array.isArray(value)) return value;
  if (isPlainObject(value)) return Object.entries(value).map(([key, item]) => ({ key, ...(isPlainObject(item) ? item : { title: key, body: item }) }));
  if (typeof value === "string") {
    return value
      .split(/\n|;|\u2022/g)
      .map((item) => item.trim())
      .filter(Boolean);
  }
  return [];
}

function splitStructuredText(text) {
  return cleanText(text, "")
    .split(/\n{2,}|\n(?=[A-Z0-9][).:-]?\s)|;|\u2022/g)
    .map((line) => line.replace(/^\s*[-*â€¢]\s*/, "").trim())
    .filter(Boolean);
}

function normalizeValueItem(item, index) {
  if (typeof item === "string") {
    return { key: `value-${index}`, title: item, body: "" };
  }
  if (!isPlainObject(item)) return null;
  const title = cleanText(item.title || item.name || item.label || item.key, "");
  const body = cleanText(item.body || item.description || item.summary || item.text || item.copy, "");
  if (!title && !body) return null;
  return {
    key: cleanText(item.key || item.id || item.slug, `value-${index}`),
    title: title || body,
    body: title ? body : "",
  };
}

function normalizeBeliefItem(item, index) {
  if (typeof item === "string") {
    return { key: `belief-${index}`, title: item, body: "", group: "", scripture: "" };
  }
  if (!isPlainObject(item)) return null;
  const title = cleanText(item.title || item.name || item.heading || item.label, "");
  const body = cleanText(item.body || item.description || item.summary || item.text || item.copy || item.statement, "");
  if (!title && !body) return null;
  return {
    key: cleanText(item.key || item.id || item.slug, `belief-${index}`),
    title: title || "Belief",
    body,
    group: cleanText(item.group || item.category || item.section, ""),
    scripture: cleanText(item.scripture || item.reference || item.verse, ""),
  };
}

function sentenceFrom(text, max = 180) {
  const normalized = cleanText(text, "").replace(/\s+/g, " ");
  if (!normalized) return "";
  const first = normalized.split(/(?<=[.!?])\s+/)[0] || normalized;
  return first.length <= max ? first : `${first.slice(0, max).replace(/\s+\S*$/, "")}...`;
}

// Legacy parser kept only to avoid migrating old saved data in place; do not use it for public rendering.
// eslint-disable-next-line no-unused-vars
function extractVisionFromStory(text) {
  const raw = cleanText(text, "");
  if (!raw) return "";
  const lines = raw
    .split(/\n+/)
    .map((line) => line.trim())
    .filter(Boolean);
  const visionIndex = lines.findIndex((line) => /\bvision\b/i.test(line));
  if (visionIndex < 0) return "";
  const headingPattern = /^(our\s+)?(mission|story|about|foundation|beliefs?)\b/i;
  const leadingLinesLookLikeVision =
    visionIndex > 0 &&
    visionIndex <= 6 &&
    lines.slice(0, visionIndex).every((line) => line.length <= 140 && !headingPattern.test(line));
  const firstLine = lines[visionIndex]
    .replace(/^(our\s+)?vision\s*[:\-–—]?\s*/i, "")
    .trim();
  const collected = leadingLinesLookLikeVision ? [...lines.slice(0, visionIndex)] : [];
  if (firstLine && firstLine.length > 10) collected.push(firstLine);
  for (const line of lines.slice(visionIndex + 1, visionIndex + 5)) {
    if (headingPattern.test(line)) break;
    collected.push(line);
  }
  return collected.join("\n").trim();
}

function collectBeliefs(org, profile, pageContentBeliefs, sourceFieldsUsed) {
  const containers = [
    ["publicProfile.pageContent.beliefs.items", pageContentBeliefs?.items],
    ["publicProfile.pageContent.beliefs.beliefs", pageContentBeliefs?.beliefs],
    ["publicProfile.pageContent.beliefs.foundation", pageContentBeliefs?.foundation],
    ["publicProfile.pageContent.beliefs.pillars", pageContentBeliefs?.pillars],
    ["destinationPages.beliefs.beliefs", org?.destinationPages?.beliefs?.beliefs],
    ["destinationPages.beliefs.items", org?.destinationPages?.beliefs?.items],
    ["websiteContent.beliefs", org?.websiteContent?.beliefs],
    ["publicProfile.beliefs", profile?.beliefs],
    ["publicProfile.beliefStatements", profile?.beliefStatements],
    ["publicProfile.faithStatements", profile?.faithStatements],
    ["publicProfile.doctrine", profile?.doctrine],
    ["org.beliefs", org?.beliefs],
    ["org.doctrine", org?.doctrine],
    ["org.faithStatements", org?.faithStatements],
  ];
  for (const [path, value] of containers) {
    const normalized = listFrom(value).map(normalizeBeliefItem).filter(Boolean);
    if (normalized.length) {
      sourceFieldsUsed.push(path);
      return normalized;
    }
  }
  return [];
}

function collectBeliefBody(org, profile, pageContentBeliefs, sourceFieldsUsed) {
  return firstField(
    [
      ["publicProfile.pageContent.beliefs.body", pageContentBeliefs?.body],
      ["publicProfile.pageContent.beliefs.description", pageContentBeliefs?.description],
      ["publicProfile.pageContent.beliefs.statement", pageContentBeliefs?.statement],
      ["publicProfile.beliefsStatement", profile?.beliefsStatement],
      ["publicProfile.statementOfFaith", profile?.statementOfFaith],
      ["publicProfile.doctrineStatement", profile?.doctrineStatement],
      ["org.beliefsStatement", org?.beliefsStatement],
      ["org.statementOfFaith", org?.statementOfFaith],
      ["org.doctrineStatement", org?.doctrineStatement],
      ["destinationPages.beliefs.body", org?.destinationPages?.beliefs?.body],
      ["websiteContent.beliefs.body", org?.websiteContent?.beliefs?.body],
    ],
    sourceFieldsUsed
  );
}

function getOrgKind(org, profile) {
  const text = normalizeComparable([
    profile?.orgType,
    profile?.organizationType,
    org?.orgType,
    org?.organizationType,
    org?.category,
    profile?.displayName,
    org?.displayName,
    org?.organizationName,
    profile?.missionStatement,
  ].filter(Boolean).join(" "));
  if (/(church|chapel|ministry|ministries|parish|fellowship|worship|pastor|rccg)/.test(text)) return "church";
  if (/(nonprofit|non profit|foundation|charity|ngo)/.test(text)) return "nonprofit";
  return "generic";
}

function getOrgDisplayLabel(org, profile) {
  return cleanText(
    profile?.publicShortName ||
      org?.publicShortName ||
      profile?.displayName ||
      profile?.organizationName ||
      profile?.churchName ||
      org?.displayName ||
      org?.organizationName ||
      org?.churchName ||
      org?.name,
    "this community"
  );
}

export function buildFoundationFallbacks(kind) {
  if (kind === "church") {
    return [
      {
        key: "scripture",
        title: "Scripture and Prayer",
        body: "We are formed by the Word of God, faithful prayer, and practical obedience.",
        group: "Our Foundation",
      },
      {
        key: "discipleship",
        title: "Worship and Discipleship",
        body: "We gather to worship, grow as followers of Christ, and serve with humility.",
        group: "Our Foundation",
      },
      {
        key: "mission",
        title: "Mission and Compassion",
        body: "We serve people with generosity, compassion, and a commitment to the good news of Jesus.",
        group: "Our Foundation",
      },
    ];
  }

  return [
    {
      key: "purpose",
      title: "Purpose",
      body: "Clear values guide how this organization serves people and stewards trust.",
      group: "Our Foundation",
    },
    {
      key: "service",
      title: "Service",
      body: "The work is shaped by practical care, accountability, and measurable community impact.",
      group: "Our Foundation",
    },
    {
      key: "trust",
      title: "Trust",
      body: "Public communication, giving, and programs are presented with clarity and integrity.",
      group: "Our Foundation",
    },
  ];
}

export function formatMissionParagraphs(text) {
  return cleanText(text, "")
    .split(/\n{2,}|\n(?=[A-Z0-9][).:-]?\s)|(?<=\.)\s+(?=[A-Z][a-z])/)
    .map((line) => line.replace(/^\s*[-*•]\s*/, "").trim())
    .filter(Boolean);
}

export function getPublicBeliefsContent(org = {}) {
  const profile = org?.publicProfile || org?.profile || {};
  const pageContentBeliefs = profile?.pageContent?.beliefs || {};
  const pageContentAbout = profile?.pageContent?.about || {};
  const sourceFieldsUsed = [];
  const orgKind = getOrgKind(org, profile);
  const orgLabel = getOrgDisplayLabel(org, profile);
  const beliefs = collectBeliefs(org, profile, pageContentBeliefs, sourceFieldsUsed);
  const body = collectBeliefBody(org, profile, pageContentBeliefs, sourceFieldsUsed);
  const mission = firstField(
    [
      ["publicProfile.pageContent.beliefs.mission", pageContentBeliefs?.mission],
      ["publicProfile.pageContent.beliefs.missionStatement", pageContentBeliefs?.missionStatement],
      ["publicProfile.pageContent.about.mission", pageContentAbout?.mission],
      ["publicProfile.pageContent.about.missionStatement", pageContentAbout?.missionStatement],
      ["org.mission", org?.mission],
      ["org.missionStatement", org?.missionStatement],
      ["publicProfile.mission", profile?.mission],
      ["publicProfile.missionStatement", profile?.missionStatement],
    ],
    sourceFieldsUsed
  );

  const vision = firstField(
    [
      ["publicProfile.pageContent.beliefs.vision", pageContentBeliefs?.vision],
      ["publicProfile.pageContent.beliefs.visionStatement", pageContentBeliefs?.visionStatement],
      ["publicProfile.pageContent.about.vision", pageContentAbout?.vision],
      ["publicProfile.pageContent.about.visionStatement", pageContentAbout?.visionStatement],
      ["org.vision", org?.vision],
      ["org.visionStatement", org?.visionStatement],
      ["publicProfile.vision", profile?.vision],
      ["publicProfile.visionStatement", profile?.visionStatement],
    ],
    sourceFieldsUsed
  );

  const fallbackMission =
    orgKind === "church"
      ? `${orgLabel} exists to help people encounter Jesus, grow in faith, and serve others with compassion.`
      : `${orgLabel} exists to serve people with clarity, care, and measurable impact.`;
  const fallbackVision =
    orgKind === "church"
      ? "A community shaped by worship, discipleship, prayer, and generous service."
      : "A trusted community where purpose, service, and stewardship move meaningful work forward.";
  // Owner decision 2026-11-04: sample text is never shown as an organization's
  // own beliefs. Sample mission, vision and foundation cards stay hidden until
  // the organization writes its own.
  void fallbackMission;
  void fallbackVision;
  const missionBody = mission || "";
  const visionBody = vision && !nearlySameText(vision, missionBody) ? vision : "";
  const fallbackBeliefs = [];
  const foundationItems = beliefs.length
    ? beliefs
    : splitStructuredText(body).length >= 2
      ? splitStructuredText(body).slice(0, 6).map((item, index) => ({
          key: `foundation-${index}`,
          title: sentenceFrom(item, 72).replace(/[.!?]$/, ""),
          body: item,
          group: "Our Foundation",
        }))
      : fallbackBeliefs;
  const foundationBody = body || (beliefs.length ? "" : fallbackBeliefs.map((item) => `${item.title}: ${item.body}`).join("\n\n"));
  const hasAdminBeliefsContent = Boolean(beliefs.length || body || mission || vision);

  return {
    eyebrow: "",
    heroTitle: cleanText(
      pageContentBeliefs?.title || org?.destinationPages?.beliefs?.title || org?.websiteContent?.beliefs?.title || profile?.beliefsTitle,
      orgKind === "church" ? "What We Believe" : "Our Principles"
    ),
    heroSubtitle: cleanText(
      pageContentBeliefs?.subtitle || org?.destinationPages?.beliefs?.subtitle || org?.websiteContent?.beliefs?.subtitle || profile?.beliefsSubtitle,
      orgKind === "church"
        ? "The convictions that guide worship, discipleship, service, and public witness."
        : "The principles that guide this organization's mission, service, and public trust."
    ),
    missionTitle: cleanText(pageContentBeliefs?.missionTitle || profile?.missionTitle || org?.missionTitle, "Mission"),
    missionBody,
    visionTitle: cleanText(pageContentBeliefs?.visionTitle || profile?.visionTitle || org?.visionTitle, "Vision"),
    visionBody,
    foundationTitle: cleanText(pageContentBeliefs?.foundationTitle || pageContentBeliefs?.bodyTitle || profile?.beliefsBodyTitle, orgKind === "church" ? "Our Foundation" : "Core Principles"),
    foundationSubtitle: cleanText(pageContentBeliefs?.foundationSubtitle || pageContentBeliefs?.description, ""),
    foundationBody,
    foundationParagraphs: formatMissionParagraphs(foundationBody),
    beliefs: foundationItems,
    sourceFieldsUsed,
    orgKind,
    usedFallback: !hasAdminBeliefsContent,
    hasContent: hasAdminBeliefsContent,
  };
}

export function getPublicMissionVisionContent(org = {}) {
  const profile = org?.publicProfile || org?.profile || {};
  const pageContentAbout = profile?.pageContent?.about || {};
  const aboutPage = org?.aboutPage || profile?.aboutPage || org?.destinationPages?.about || org?.websiteContent?.about || {};
  const sourceFieldsUsed = [];
  const warnings = [];

  const missionBody = firstField(
    [
      ["publicProfile.pageContent.about.missionBody", pageContentAbout?.missionBody],
      ["publicProfile.pageContent.about.mission", pageContentAbout?.mission],
      ["publicProfile.pageContent.about.missionStatement", pageContentAbout?.missionStatement],
      ["org.fullMissionStatement", org?.fullMissionStatement],
      ["publicProfile.fullMissionStatement", profile?.fullMissionStatement],
      ["org.missionStatement", org?.missionStatement],
      ["publicProfile.missionStatement", profile?.missionStatement],
    ],
    sourceFieldsUsed,
    warnings
  );

  const visionBody = firstField(
    [
      ["publicProfile.pageContent.about.visionBody", pageContentAbout?.visionBody],
      ["publicProfile.pageContent.about.vision", pageContentAbout?.vision],
      ["publicProfile.pageContent.about.visionStatement", pageContentAbout?.visionStatement],
      ["org.vision", org?.vision],
      ["org.visionStatement", org?.visionStatement],
      ["publicProfile.vision", profile?.vision],
      ["publicProfile.visionStatement", profile?.visionStatement],
      ["websiteContent.vision", org?.websiteContent?.vision],
    ],
    sourceFieldsUsed
  );

  const storyBody = firstDistinctField(
    [
      ["publicProfile.pageContent.about.churchStory", pageContentAbout?.churchStory],
      ["publicProfile.pageContent.about.aboutStory", pageContentAbout?.aboutStory],
      ["publicProfile.aboutStory", profile?.aboutStory],
      ["org.aboutStory", org?.aboutStory],
      ["publicProfile.pageContent.about.body", pageContentAbout?.body],
      ["org.longMissionStory", org?.longMissionStory],
      ["publicProfile.longMissionStory", profile?.longMissionStory],
      ["org.churchStory", org?.churchStory],
      ["org.story", org?.story],
      ["publicProfile.story", profile?.story],
      ["aboutPage.body", aboutPage?.body],
      ["aboutPage.introduction", aboutPage?.introduction],
      ["websiteContent.about.body", org?.websiteContent?.about?.body],
      ["destinationPages.about.body", org?.destinationPages?.about?.body],
    ],
    [missionBody, visionBody],
    sourceFieldsUsed
  );

  const aboutBody = firstDistinctField(
    [
      ["publicProfile.pageContent.about.churchStory", pageContentAbout?.churchStory],
      ["publicProfile.pageContent.about.aboutStory", pageContentAbout?.aboutStory],
      ["publicProfile.pageContent.about.body", pageContentAbout?.body],
      ["aboutPage.body", aboutPage?.body],
      ["aboutPage.introduction", aboutPage?.introduction],
      ["destinationPages.about.body", org?.destinationPages?.about?.body],
      ["websiteContent.about.body", org?.websiteContent?.about?.body],
      ["org.longMissionStory", org?.longMissionStory],
      ["org.churchStory", org?.churchStory],
      ["org.story", org?.story],
    ],
    [missionBody, visionBody],
    sourceFieldsUsed
  );

  const rawValues =
    listFrom(org?.values).length ? org?.values :
    listFrom(profile?.values).length ? profile?.values :
    listFrom(org?.coreValues).length ? org?.coreValues :
    listFrom(org?.websiteContent?.values).length ? org?.websiteContent?.values :
    [];
  const values = listFrom(rawValues)
    .map(normalizeValueItem)
    .filter(Boolean)
    .filter((value) => {
      const combined = [value.title, value.body].filter(Boolean).join(" ");
      return ![missionBody, visionBody, storyBody, aboutBody].some((item) => nearlySameText(combined, item));
    })
    .slice(0, 6);
  if (values.length) sourceFieldsUsed.push("values");

  const fallbackMission = "We exist to help people encounter Jesus, grow in faith, serve with compassion, and carry hope into everyday life.";
  const fallbackVision = "A community shaped by worship, discipleship, prayer, and generous service.";
  const hasAdminMissionContent = Boolean(missionBody || visionBody || storyBody || aboutBody || values.length);
  const fallbackStory = "";
  const normalized = {
    eyebrow: "",
    heroTitle: cleanText(
      pageContentAbout?.heroTitle || pageContentAbout?.title || aboutPage?.heroTitle || aboutPage?.title,
      "Mission & Vision"
    ),
    heroSubtitle: "",
    sectionHeadline: "",
    sectionIntro: "",
    missionTitle: cleanText(pageContentAbout?.missionTitle || org?.missionTitle || profile?.missionTitle, "Mission"),
    missionBody: missionBody || fallbackMission,
    visionTitle: cleanText(org?.visionTitle || profile?.visionTitle, "Vision"),
    visionBody: visionBody || fallbackVision,
    storyTitle: cleanText(pageContentAbout?.title || aboutPage?.title || org?.storyTitle || profile?.storyTitle, "Our Story"),
    storyBody: storyBody || aboutBody || fallbackStory,
    aboutTitle: cleanText(pageContentAbout?.title || aboutPage?.title, ""),
    aboutSubtitle: cleanText(pageContentAbout?.subtitle || aboutPage?.subtitle || aboutPage?.intro || aboutPage?.eyebrow, ""),
    aboutBody: aboutBody || storyBody || "",
    values: values.length
      ? values
      : [
          { key: "worship", title: "Worship", body: "A life centered on God with reverence and joy." },
          { key: "discipleship", title: "Discipleship", body: "Growing in faith, Scripture, prayer, and obedience." },
          { key: "compassion", title: "Compassion", body: "Serving people with humility, care, and generosity." },
        ],
    ctas: [
      { key: "visit", label: "Plan Your Visit", href: "new-here" },
      { key: "give", label: "Give", href: "donate" },
      { key: "contact", label: "Contact Us", href: "contact" },
    ],
    sourceFieldsUsed: Array.from(new Set(sourceFieldsUsed)),
    warnings,
    hasAdminMissionContent,
    usedFallback: !hasAdminMissionContent,
  };

  if (process.env.NODE_ENV !== "production") {
    console.debug("[Lightly Mission Content]", {
      found: normalized.sourceFieldsUsed,
      rendered: {
        mission: Boolean(normalized.missionBody),
        vision: Boolean(normalized.visionBody),
        story: Boolean(normalized.storyBody),
        values: normalized.values.length,
      },
      usedFallback: normalized.usedFallback,
      warnings: normalized.warnings,
    });
  }

  return normalized;
}
